<script>
import _ from "lodash";
import { firestore, functions } from "@/services/firebase";
import GameReferee from "./GameReferee";
import { GameRefereeBase } from "vue-chess-shared/GameRefereeBase";
import { GAME_RESULT, MATCH_STATUS } from "vue-chess-shared/constants";
import Chess from "chess.js";
import { getPlayerColor } from "@/utils/game-helper";
import { mapGetters } from "vuex";
import { MatchesAPI } from "@/services/api";
import { serverTimestamp } from "@/utils/firebase-helper";

const handleTimeoutServer = functions.httpsCallable(
  "referee-handleTimeoutServer"
);
const handleGameOverServer = functions.httpsCallable(
  "referee-handleGameOverServer"
);

class GameRefereeFirestoreLimited extends GameRefereeBase {
  engine() {
    return new Chess();
  }

  firestore() {
    return firestore;
  }

  serverTimestamp() {
    return serverTimestamp();
  }

  setData(match, moves) {
    this.match = { ...match };
    this.moves = [...moves];
    this.loadEngine();
  }

  handleCommence() {
    // do not commence from client
  }

  async updateMatch(payload) {
    console.log("handle update match", payload);
    if ([GAME_RESULT.TIMEOUT, GAME_RESULT.ABORT].includes(payload.result)) {
      // when timeout let the server decide when the match is over
      console.log("finalising match because of timeout/abort");
      if (this.handleFinalizing) {
        this.handleFinalizing(this.turnColor(), handleTimeoutServer);
      }
      return;
    }

    if (
      [
        GAME_RESULT.CHECKMATE,
        GAME_RESULT.STALEMATE,
        GAME_RESULT.THREEFOLD_REPETITION,
        GAME_RESULT.INSUFFICIENT_MATERIAL,
        GAME_RESULT.DRAW,
        GAME_RESULT.GAME_OVER,
        GAME_RESULT.BARE_KING,
        GAME_RESULT.FORFEIT,
      ].includes(payload.result)
    ) {
      console.log("finalising match because of game over");
      if (this.handleFinalizing) {
        this.handleFinalizing(this.turnColor(), handleGameOverServer);
      }

      return;
    }

    // Remaining are: DRAW_BY_AGREEMENT, RESIGN, TIMEOUT, WHITE_ABORT, BLACK_ABORT
    // These can be decided at client side
    this.match.result = payload.result;
    await super.updateMatch(payload);
    await this.updateMatchSql(payload);
  }

  async updateMatchSql(payload) {
    const {
      player_1_score: whiteScore,
      player_2_score: blackScore,
      result,
    } = payload;

    // no need save match before there is a result
    if (!result) return;

    const gameOverride = this.match.gameOverride;
    // Save to SQL DB
    const updatedMatch = await MatchesAPI.putMatch(
      this.matchId,
      gameOverride,
      whiteScore ?? null,
      blackScore ?? null,
      result,
      this.moves.length
    );

    console.debug("SQL updatedMatch", updatedMatch);
  }

  setSchedule(name, color, seconds) {
    if (!this.timers) {
      this.timers = [];
    }

    const timerId = setTimeout(() => {
      if (name === "handleLeewayOver") {
        this.handleAbortOrCommence(color);
      } else if (name === "handleMatchTimeout") {
        this.handleTimeoutOrForfeit(color);
      }
    }, seconds * 1000);

    console.debug("add schedule with id", name, timerId);
    this.timers.push(timerId);
  }

  clearSchedules() {
    console.debug("clearSchedules", this.timers);
    this.timers?.forEach((t) => clearTimeout(t));
    this.timers = [];
  }

  onFinalizing(handler) {
    this.handleFinalizing = handler;
  }
}

export default {
  name: "GameRefereeSecondary",

  extends: GameReferee,

  data() {
    return {
      match: null,
      moves: null,
    };
  },

  computed: {
    ...mapGetters({
      userId: "getUserId",
      onlinePlayers: "getOnlinePlayers",
    }),
  },

  watch: {
    matchId: {
      handler() {
        if (!this.matchId) return;
        this.setup();
      },
      immediate: true,
    },

    match: {
      deep: true,
      handler() {
        this.handleMatch();
      },
    },

    moves: {
      deep: true,
      handler() {
        this.handleMove();
      },
    },
  },

  methods: {
    setup() {
      this.matchRef = firestore.collection("matches").doc(this.matchId);
      this.$bind("match", this.matchRef);
      this.$bind("moves", this.matchRef.collection("moves"));
    },

    handleMatch: _.debounce(async function() {
      if (
        [MATCH_STATUS.FINISHED, MATCH_STATUS.ABORTED].includes(
          this.match.status
        )
      ) {
        this.referee.clearSchedules();
        this.$emit("onGameFinalized");
        return;
      }
      this.referee.setData(this.match, this.moves);
      await this.referee.checkAbortOrTimeout();
      await this.referee.checkMatchUserAction();
    }, 500),

    handleMove: _.debounce(async function() {
      this.cancelFinalizing();
      this.referee.setData(this.match, this.moves);
      await this.referee.checkLastMoveUserAction();
      await this.referee.handleMove();
    }, 500),

    handleFinalizing(turnColor, serverHandler) {
      this.$emit("onGameFinalizing");

      if (turnColor === getPlayerColor(this.match, this.userId)) {
        setTimeout(async () => {
          if (this.match.result) {
            return;
          }
          console.debug(`${turnColor} call server to handle result`);
          await serverHandler({ matchId: this.matchId, color: turnColor });
        }, 2000);
      } else {
        setTimeout(async () => {
          if (this.match.result) {
            return;
          }

          const nextColor = this.referee.nextColor();
          console.debug(`${nextColor} call server to handle result`);
          await serverHandler({
            matchId: this.matchId,
            color: nextColor,
          });
        }, 6000);
      }
    },

    cancelFinalizing() {
      this.$emit("onCancelFinalizing");
    },
  },

  beforeCreate() {
    this.referee = new GameRefereeFirestoreLimited();
  },

  created() {
    this.referee.onFinalizing(this.handleFinalizing);
  },
};
</script>
